/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonsModule} from 'ngx-bootstrap/buttons';
import template from './notification-preferences-edit-page.component.html';
import {APP_SHARED} from '../../common/app-shared';
import {FORM_CONTROLS} from '../../common/directives/form-controls/form-controls';
import {SignalAdminResourceEditPageBase} from '../../common/controller/signal-admin-resource-edit-page-base';
import {LocalNetworkProfile} from '../../../components/session/local-network-profile';
import {LocalUserProfile} from '../../../components/session/local-user-profile';
import {
  NotificationPreferencesApiService,
  NotificationPreferencesModel
} from './notification-preferences-api.service';

interface NotificationMessageConfig {
  id: string;
  sev: string;
}

@Component({
  standalone: true,
  selector: 'adn-notification-preferences-edit-page',
  imports: [...APP_SHARED, FormsModule, TranslateModule, ButtonsModule, ...FORM_CONTROLS],
  template
})
export class NotificationPreferencesEditPageComponent extends SignalAdminResourceEditPageBase<NotificationPreferencesModel> {
  readonly notificationLevels = ['INFO', 'WARNING', 'IMPORTANT', 'ERROR'];
  readonly notificationObjects = ['LINE_ITEM', 'CREATIVE', 'REPORT_EXECUTION', 'DATA_EXPORT'];
  readonly notificationMethods = ['EMAIL', 'UI'];
  readonly notificationMessages: NotificationMessageConfig[];

  constructor(
    @Inject(LocalNetworkProfile) LocalNetworkProfile: any,
    @Inject(LocalUserProfile) private localUserProfile: any,
    api: NotificationPreferencesApiService
  ) {
    super(api, 'notificationPreferences');
    this.notificationMessages = [
      {id: 'LINE_ITEM_READY', sev: 'INFO'},
      {id: 'LINE_ITEM_RUNNING', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_STOPPED_RUNNING', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_ENDED_END_DATE_PASSED', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_ENDED_OBJECTIVE_REACHED', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_NOT_STARTED', sev: 'WARNING'},
      {id: 'LINE_ITEM_DELETED', sev: 'INFO'},
      {id: 'LINE_ITEM_SUBMITTED', sev: 'IMPORTANT'},
      {id: 'LINE_ITEM_REJECTED', sev: 'IMPORTANT'},
      {id: 'CREATIVE_REJECTED', sev: 'IMPORTANT'},
      {id: 'REPORT_COMPLETE', sev: 'INFO'},
      {id: 'DATA_EXPORT_HAS_FAILED', sev: 'ERROR'}
    ];

    if (LocalNetworkProfile.isSuiPlatform()) {
      this.notificationMessages.push({id: 'NEW_SIGNUP_USER', sev: 'INFO'});
    }
  }

  /** Returns (and lazily initialises) the overrides array for a message id. */
  getOverrides(messageId: string): string[] {
    this.editingModel.overrides = this.editingModel.overrides || {};
    this.editingModel.overrides[messageId] = this.editingModel.overrides[messageId] || [];
    return this.editingModel.overrides[messageId];
  }

  // ── Validation ───────────────────────────────────────────────────────────────

  hasMethodValidationError(objectType: string): boolean {
    const subscription = this.getSubscription(objectType);
    return subscription.subscribed === true && subscription.deliveryMethods.length === 0;
  }

  hasSeverityValidationError(objectType: string): boolean {
    const subscription = this.getSubscription(objectType);
    return subscription.subscribed === true && !subscription.severityLevel;
  }

  hasAnySubscriptionValidationError(): boolean {
    return this.notificationObjects.some(
      (objectType) => this.hasMethodValidationError(objectType) || this.hasSeverityValidationError(objectType)
    );
  }

  // ── Lifecycle ────────────────────────────────────────────────────────────────

  protected override initialiseModel(model: NotificationPreferencesModel): NotificationPreferencesModel {
    const initialised = super.initialiseModel(model);
    this.initModel(initialised);
    return initialised;
  }

  protected override prepareForSubmit(model: NotificationPreferencesModel): void {
    model.user = {id: this.localUserProfile.get('userId')};

    // Unsubscribed types must be omitted: core requires severityLevel on every
    // remaining subscription entry (empty deliveryMethods is not enough).
    this.notificationObjects.forEach((objectType) => {
      const subscription = this.getSubscription(objectType);
      if (!subscription.subscribed) {
        delete model.subscriptions[objectType];
        return;
      }
      delete subscription.editing;
    });
  }

  // ── Private ──────────────────────────────────────────────────────────────────

  private initModel(model: NotificationPreferencesModel): void {
    model.subscriptions = model.subscriptions || {};
    model.overrides = model.overrides || {};

    this.notificationObjects.forEach((objectType) => {
      const subscription = this.getModelSubscription(model, objectType);
      subscription.subscribed = subscription.deliveryMethods.length > 0;
    });

    model.user = {id: this.localUserProfile.get('userId')};
  }

  private getSubscription(objectType: string): any {
    return this.getModelSubscription(this.editingModel, objectType);
  }

  private getModelSubscription(model: NotificationPreferencesModel, objectType: string): any {
    model.subscriptions = model.subscriptions || {};
    model.subscriptions[objectType] = model.subscriptions[objectType] || {};
    model.subscriptions[objectType].deliveryMethods = model.subscriptions[objectType].deliveryMethods || [];
    return model.subscriptions[objectType];
  }
}
